// @givmo/board — a seat's terms on the member page's "Their service" card (CBG-YR3-TERMS, frame t1;
// the backend's contract `board-terms.md` §5, §13).
//
// Current first. Each term is the ORGANIZATION's account: its dates, its class, whether the board
// counts it toward its limit, and where it came from — the meeting that elected the person, the
// written consent, the minute-book record, or who entered it. A source is named only as far as this
// reader may read it (§5.2): a closed session's motion outside the reader's reach is served as nothing,
// and so says nothing here. The current term's passed end is the approved holdover form — "Since …
// · Term ended … – still serving" — and changes nothing about what the person may do. Below the terms,
// only when the board recorded a limit, one line sets the board's own count beside its own limit
// (§11.1), and says nothing about whether anyone may serve again. The Owner corrects a term from its
// row; "Record a term" sits in the card's heading.

import type { BoardMemberTermOut, OrgBoardMemberOut } from '@givmo/api-client';
import { FOCUS_RING } from '@givmo/design-system';
import { Link } from '../../board-routing';
import { displayDate } from '../../lib/display';
import { keptTogether } from './officer-roles';
import { officerRolesL3Words as l3 } from './officer-roles-l3-words';
import { countedTerms, heldOverEnd, seatTerms, type TermRule } from './terms-decode';
import { termsWords as tw } from './terms-words';

const QUIET = 'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const SECONDARY = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
/** A source link is underlined already, so its focus is the house ring, never an underline. */
const SOURCE_LINK = `rounded-sm text-text-secondary underline decoration-border-default underline-offset-2 hover:text-text-strong ${FOCUS_RING}`;
/** "Correct" in the row's own quiet weight, like its source link: a term's correction is an edit of
 *  the organization's record, never a call to act, so it doesn't take the brand's colour. */
const CORRECT = `ml-auto rounded-sm font-sans text-s12 font-w400 tracking-givmo text-text-secondary underline decoration-border-default underline-offset-2 hover:text-text-strong ${FOCUS_RING}`;

/** A term's dates as one line: a range, a start, an end, or nothing recorded. */
export function termDates(term: Pick<BoardMemberTermOut, 'starts_on' | 'ends_on'>): string {
  if (term.starts_on && term.ends_on)
    return tw.range(displayDate(term.starts_on), displayDate(term.ends_on));
  if (term.starts_on) return tw.from(displayDate(term.starts_on));
  if (term.ends_on) return tw.until(displayDate(term.ends_on));
  return '—';
}

/** Where a term came from, named only as far as the reader may read it (§5.2). */
function TermSource({ term }: { term: BoardMemberTermOut }) {
  const source = term.source ?? null;
  const label = source?.title?.trim() || displayDate(source?.on ?? null);
  if (term.source_kind === 'motion' && source?.meeting_id) {
    return (
      <span className={QUIET}>
        {tw.electedAt}{' '}
        <Link
          to="/meetings/$meetingId/record"
          params={{ meetingId: source.meeting_id }}
          className={SOURCE_LINK}
          data-testid={`member-term-source-${term.id}`}
        >
          {label}
        </Link>
      </span>
    );
  }
  if (term.source_kind === 'written_consent' && source !== null) {
    return (
      <span className={QUIET}>
        {tw.electedByConsent}{' '}
        <Link
          to="/consents/$consentId"
          params={{ consentId: source.id }}
          className={SOURCE_LINK}
          data-testid={`member-term-source-${term.id}`}
        >
          {label}
        </Link>
      </span>
    );
  }
  if (term.source_kind === 'record' && source !== null) {
    return (
      <span className={QUIET}>
        {tw.fromTheBook}{' '}
        <Link
          to="/records/$recordId"
          params={{ recordId: source.id }}
          className={SOURCE_LINK}
          data-testid={`member-term-source-${term.id}`}
        >
          {label}
        </Link>
      </span>
    );
  }
  if (term.source_kind === 'carried_over') return <span className={QUIET}>{tw.carriedOver}</span>;
  if (term.source_kind === 'entered' && term.recorded_by_display_name) {
    return <span className={QUIET}>{tw.enteredBy(term.recorded_by_display_name)}</span>;
  }
  return null;
}

export function SeatTermsList({
  seat,
  limit,
  today,
  onCorrect,
}: {
  seat: OrgBoardMemberOut;
  /** The board's recorded limit on consecutive terms, if any: the count line shows only with one. */
  limit: TermRule | null;
  /** The reader's calendar day (`todayAsDateInput`). */
  today: string;
  /** The Owner's "Correct", per term; absent for everyone else. */
  onCorrect?: (term: BoardMemberTermOut) => void;
}) {
  const terms = seatTerms(seat);
  if (terms.length === 0) return null;
  return (
    <div className="flex flex-col gap-10" data-testid="member-terms">
      <ul className="flex flex-col">
        {terms.map((term, index) => {
          const heldOver = index === 0 ? heldOverEnd(term, seat, today) : null;
          // The approved line, split once at its "·": the start reads as the term's own, the passed
          // end in the quieter weight beside it.
          const [since, ended] =
            heldOver === null
              ? [null, null]
              : term.starts_on
                ? l3.heldOverDates(displayDate(term.starts_on), displayDate(heldOver)).split(' · ')
                : [null, l3.termEndedStillServing(displayDate(heldOver))];
          const quietLine = [
            term.counts_toward_limit === false && limit !== null ? tw.notCounted : null,
            term.note?.trim() || null,
          ].filter((part): part is string => part !== null);
          return (
            <li
              key={term.id}
              className="flex flex-col gap-4 border-t border-border-subtle py-12 first:border-t-0 first:pt-0"
              data-testid={`member-term-${term.id}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-2">
                {ended ? (
                  // The approved member-page form for a held-over term: its start, then the end
                  // that passed, once — never as a range that reads as over (HOLDOVER-PAGE). The
                  // held-over phrase stays on one line, so a phone breaks after the "·".
                  <span className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
                    {since ? <span className="font-w600 text-text-strong">{since} · </span> : null}
                    <span data-testid="member-term-held-over">{keptTogether(ended)}</span>
                  </span>
                ) : (
                  <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    {termDates(term)}
                  </span>
                )}
                {term.class_label ? (
                  <span
                    className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary"
                    data-testid={`member-term-class-${term.id}`}
                  >
                    {term.class_label}
                  </span>
                ) : null}
              </div>
              {quietLine.length > 0 ? (
                <span className={SECONDARY}>{quietLine.join(' · ')}</span>
              ) : null}
              <div className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-2">
                <TermSource term={term} />
                {onCorrect !== undefined ? (
                  <button
                    type="button"
                    className={CORRECT}
                    aria-label={tw.correctFor(termDates(term))}
                    onClick={() => onCorrect(term)}
                    data-testid={`member-term-correct-${term.id}`}
                  >
                    {tw.correct}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {limit !== null ? (
        <p
          className={`border-t border-border-subtle pt-10 ${SECONDARY}`}
          data-testid="member-terms-counted"
        >
          {tw.counted(countedTerms(seat), limit.value, limit.citation)}
        </p>
      ) : null}
    </div>
  );
}
